/**
 * Cookie utility functions for secure token management
 * Uses HttpOnly cookies to prevent XSS attacks
 */

export interface CookieOptions {
  expires?: Date | number;
  maxAge?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
  httpOnly?: boolean;
}

export interface TokenData {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

/**
 * Set a cookie with secure defaults
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {},
): void => {
  const {
    expires,
    maxAge,
    domain,
    path = "/",
    secure = true,
    sameSite = "strict",
    httpOnly = false,
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  // Add path
  cookieString += `; path=${path}`;

  // Add domain if specified
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Add expiration
  if (expires) {
    if (expires instanceof Date) {
      cookieString += `; expires=${expires.toUTCString()}`;
    } else {
      const date = new Date();
      date.setTime(date.getTime() + expires * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }
  }

  // Add max age
  if (maxAge) {
    cookieString += `; max-age=${maxAge}`;
  }

  // Add secure flag (HTTPS only)
  if (secure) {
    cookieString += "; secure";
  }

  // Add same site
  cookieString += `; samesite=${sameSite}`;

  // Note: httpOnly can only be set by the server
  // This is a client-side utility, so we can't set httpOnly here
  // The server should set httpOnly when sending cookies

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie?.charAt(0) === " ") {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie?.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }
  return null;
};

/**
 * Delete a cookie
 */
export const deleteCookie = (
  name: string,
  options: CookieOptions = {},
): void => {
  const { domain, path = "/" } = options;

  // Set expiration to past date to delete
  setCookie(name, "", {
    ...options,
    expires: new Date(0),
    domain,
    path,
  });
};

/**
 * Check if cookies are supported
 */
export const areCookiesSupported = (): boolean => {
  try {
    const testKey = "__cookie_test__";
    const testValue = "test";

    setCookie(testKey, testValue);
    const retrieved = getCookie(testKey);
    deleteCookie(testKey);

    return retrieved === testValue;
  } catch {
    return false;
  }
};

/**
 * Get all cookies as an object
 */
export const getAllCookies = (): Record<string, string> => {
  const cookies: Record<string, string> = {};

  if (document.cookie) {
    document.cookie.split(";").forEach((cookie) => {
      const trimmedCookie = cookie.trim();
      const [name, value] = trimmedCookie.split("=");
      if (name && value) {
        cookies[decodeURIComponent(name)] = decodeURIComponent(value);
      }
    });
  }

  return cookies;
};

/**
 * Clear all cookies (use with caution)
 */
export const clearAllCookies = (): void => {
  const cookies = getAllCookies();
  Object.keys(cookies).forEach((name) => {
    deleteCookie(name);
  });
};

/**
 * Token-specific cookie utilities
 */
export const TOKEN_COOKIE_NAME = "auth_token";
export const REFRESH_TOKEN_COOKIE_NAME = "refresh_token";

/**
 * Set authentication tokens as cookies
 */
export const setAuthTokens = (tokens: TokenData): void => {
  const { accessToken, refreshToken, expiresIn } = tokens;

  // Set access token cookie
  setCookie(TOKEN_COOKIE_NAME, accessToken, {
    expires: expiresIn,
    secure: true,
    sameSite: "strict",
  });

  // Set refresh token cookie if provided
  if (refreshToken) {
    setCookie(REFRESH_TOKEN_COOKIE_NAME, refreshToken, {
      expires: 30 * 24 * 60 * 60, // 30 days
      secure: true,
      sameSite: "strict",
    });
  }
};

/**
 * Get authentication tokens from cookies
 */
export const getAuthTokens = (): Partial<TokenData> => {
  const accessToken = getCookie(TOKEN_COOKIE_NAME);
  const refreshToken = getCookie(REFRESH_TOKEN_COOKIE_NAME);

  return {
    ...(accessToken && { accessToken }),
    ...(refreshToken && { refreshToken }),
  };
};

/**
 * Clear authentication tokens
 */
export const clearAuthTokens = (): void => {
  deleteCookie(TOKEN_COOKIE_NAME);
  deleteCookie(REFRESH_TOKEN_COOKIE_NAME);
};

/**
 * Check if user has valid authentication tokens
 */
export const hasAuthTokens = (): boolean => {
  const tokens = getAuthTokens();
  return !!(tokens.accessToken || tokens.refreshToken);
};

/**
 * JWT Payload interface
 */
export interface JWTPayload {
  sub?: string;
  iss?: string;
  aud?: string;
  exp?: number;
  iat?: number;
  nbf?: number;
  jti?: string;
  [key: string]: unknown;
}

/**
 * Parse JWT token to get payload (for client-side validation)
 * Note: This is for basic validation only, server should always validate
 */
export const parseJWT = (token: string): JWTPayload | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    const base64: any = base64Url?.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = parseJWT(token);
  if (!payload?.exp) return true;

  return payload.exp * 1000 < Date.now();
};

/**
 * Get token expiration time
 */
export const getTokenExpiration = (token: string): Date | null => {
  const payload = parseJWT(token);
  if (!payload?.exp) return null;

  return new Date(payload.exp * 1000);
};

/**
 * Check if token will expire soon (within specified minutes)
 */
export const isTokenExpiringSoon = (
  token: string,
  minutes: number = 5,
): boolean => {
  const expiration = getTokenExpiration(token);
  if (!expiration) return true;

  const now = new Date();
  const threshold = new Date(now.getTime() + minutes * 60 * 1000);

  return expiration <= threshold;
};
